// The connect-time choice stays bound to the account that this attempt creates.
import type {
  ConnectionReadItem,
  ConnectionReadOperation,
  ConnectionAccessChoice,
} from '../connection-read-access/api';
import type { ScopedConnectionItem } from '../connection-api';

/** What an app's reviewed reads are before an account is connected (server projection). */
export interface ReadCatalogue {
  operations: ConnectionReadOperation[];
}

/** The choice made in the connect sheet, captured for exactly this person, company and app. */
export interface PendingChoice {
  /** The page's [company id, user id] pair when the choice was made. */
  scope: string;
  appId: string;
  audience: ScopedConnectionItem['audience'];
  choice: ConnectionAccessChoice;
}

export type AfterConnect =
  | { kind: 'save'; read: ConnectionReadItem; choice: ConnectionAccessChoice }
  | { kind: 'ask'; reason: string };

/**
 * What to do once a connect finished: save the chosen access only for the exact account this
 * connect added, under the same person and company, while its access is still untouched.
 * Anything missing or ambiguous leaves access unchanged.
 */
export function afterConnect(
  choice: PendingChoice | null,
  scope: string,
  item: ScopedConnectionItem,
  previousIds: Set<string | null>,
  /** How many new connected accounts for this app and audience the reconcile shows. */
  added = 1,
): AfterConnect {
  if (!choice) return { kind: 'ask', reason: 'chosen' };
  if (choice.scope !== scope) return { kind: 'ask', reason: 'scope_changed' };
  if (choice.appId !== item.id || choice.audience !== item.audience)
    return { kind: 'ask', reason: 'other_app' };
  if (!item.connectionId || previousIds.has(item.connectionId))
    return { kind: 'ask', reason: 'not_a_new_account' };
  if (added !== 1) return { kind: 'ask', reason: 'ambiguous' };
  const read = item.readAccess;
  if (!read || read.connectionId !== (item.mcpConnectionId ?? item.connectionId))
    return { kind: 'ask', reason: 'no_binding' };
  if (!read.canSet || read.access.revision !== null || read.access.enabled || read.access.status)
    return { kind: 'ask', reason: 'not_untouched' };
  return { kind: 'save', read, choice: choice.choice };
}
